import { useState, useRef, useEffect, useCallback } from "react";
import { Label, Input, Button, Skeleton } from "@pushpress/pushpress-ui";
import { CheckIcon, XIcon, Pencil } from "lucide-react";
import { EMPTY_VALUE } from "../../constants";

type EditableFieldProps = {
  label: string;
  value?: string | null;
  onSave: (value: string) => void;
  hasData?: boolean;
  type?: "text" | "number" | "email" | "phone";
  validation?: (value: string) => boolean;
};

export default function EditableInputField({
  label,
  value = "",
  onSave,
  hasData = false,
  type = "text",
  validation,
}: EditableFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [error, setError] = useState<boolean | string>(false);
  const editFieldRef = useRef<HTMLDivElement>(null);

  const handleSave = () => {
    if (validation && !validation(editValue)) {
      setError(`Valid ${label} is required`);
      return;
    }
    onSave(editValue.trim());
    setIsEditing(false);
  };

  const handleCancel = useCallback(() => {
    setEditValue(value);
    setIsEditing(false);
    if (error) {
      setError(false);
    }
  }, [error, value]);

  const handleChange = (value: string) => {
    setEditValue(value);
    setError(false);
  };

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  // Handle click outside to exit edit mode
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        editFieldRef.current &&
        !editFieldRef.current.contains(event.target as Node)
      ) {
        handleCancel();
      }
    }

    if (isEditing) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing, value, handleCancel]);

  if (!hasData) {
    return (
      <div className="tw-group">
        <Label className="tw-text-xs tw-text-neutral-600">{label}</Label>
        <Skeleton className="tw-h-4 tw-w-1/2" />
      </div>
    );
  }

  return (
    <div className="tw-group tw-h-12">
      <Label className="tw-text-xs tw-text-neutral-600">{label}</Label>
      {isEditing ? (
        <>
          <div ref={editFieldRef} className="tw-flex tw-items-center">
            <Input
              data-testid="input-field-editable"
              type={type}
              value={editValue}
              onChange={(e) => handleChange(e.target.value)}
              className="tw-h-8"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSave();
                if (e.key === "Escape") handleCancel();
              }}
            />
            <Button
              data-testid="input-save-button"
              variant="ghost"
              size="icon"
              className="tw-h-7 tw-w-7"
              onClick={handleSave}
              onKeyDown={handleSave}
              role="button"
              tabIndex={0}
            >
              <CheckIcon className="tw-h-4 tw-w-4 tw-text-success" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="tw-h-7 tw-w-7"
              onClick={handleCancel}
            >
              <XIcon className="tw-h-4 tw-w-4 tw-text-error" />
            </Button>
          </div>
          <div className="tw-h-4">
            {error && <div className="tw-text-xs tw-text-error">{error}</div>}
          </div>
        </>
      ) : (
        <div
          data-testid="input-field-view"
          className="tw-cursor-pointer tw-py-1 hover:tw-bg-neutral-100 tw-transition-colors tw-group tw-flex tw-items-center"
          onClick={() => setIsEditing(true)}
          onKeyDown={() => setIsEditing(true)}
          role="button"
          tabIndex={0}
        >
          <p className="tw-font-medium group-hover:tw-text-primary tw-transition-colors">
            {value || EMPTY_VALUE}
          </p>
          <div className="tw-ml-2 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity">
            <Pencil className="tw-h-4 tw-w-4 tw-text-neutral-600" />
          </div>
        </div>
      )}
    </div>
  );
}
